/**
 * Flash Deals — portal-managed flash discounts (same rules as legacy Create Product flash).
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { FaBolt, FaTrash, FaSearch, FaTimes, FaCheck, FaBoxes } from "react-icons/fa";
import { FiEdit2 } from "react-icons/fi";
import {
    getFlashDealProducts,
    getProductsForFlashDealMapping,
    addFlashDealProduct,
    updateFlashDealProduct,
    removeFlashDealProduct,
    getFlashDiscountSettings,
    toggleFlashDealAutoScroll,
} from "../../services/ClientService";
import { toast } from "react-toastify";
import { getOptionLabel, getOptionPriceHints } from "../../utils/dealOptionDisplay";

const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
};

const getProductPreview = (item) => {
    if (!item) return null;
    if (item.preview) return item.preview;
    if (item.images?.preview) return item.images.preview;
    if (Array.isArray(item.images) && item.images[0]?.preview) return item.images[0].preview;
    if (item.productImage?.preview) return item.productImage.preview;
    return null;
};

const FlashDeals = () => {
    const [entries, setEntries] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [listSearch, setListSearch] = useState("");
    const debouncedListSearch = useDebounce(listSearch, 400);

    const [productSearch, setProductSearch] = useState("");
    const debouncedProductSearch = useDebounce(productSearch, 500);
    const [availableProducts, setAvailableProducts] = useState([]);
    const [productsSkip, setProductsSkip] = useState(0);
    const [productsLimit] = useState(10);
    const [hasMoreProducts, setHasMoreProducts] = useState(true);
    const [isLoadingProducts, setIsLoadingProducts] = useState(false);
    const [isFetchingMore, setIsFetchingMore] = useState(false);

    const [configModalOpen, setConfigModalOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedOption, setSelectedOption] = useState(null);
    const [editingEntry, setEditingEntry] = useState(null);
    const [discountPrice, setDiscountPrice] = useState("");
    const [minimumCartValue, setMinimumCartValue] = useState("");
    const [maxQty, setMaxQty] = useState("1");
    const [isSaving, setIsSaving] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [enableAutoScroll, setEnableAutoScroll] = useState(false);
    const [autoScrollIntervalMs, setAutoScrollIntervalMs] = useState(3000);
    const [scrollMsDraft, setScrollMsDraft] = useState("3000");
    const [autoScrollSaving, setAutoScrollSaving] = useState(false);
    const DEFAULT_SCROLL_MS = 3000;

    const excludeKeys = useMemo(
        () => entries.map((e) => `${e.productId}_${e.selectedWeight}`),
        [entries]
    );

    const excludeKeysKey = useMemo(() => excludeKeys.join("|"), [excludeKeys]);

    const fetchEntries = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await getFlashDealProducts({ searchTerm: debouncedListSearch });
            if (res.success) setEntries(res.data || []);
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    }, [debouncedListSearch]);

    useEffect(() => { fetchEntries(); }, [fetchEntries]);

    useEffect(() => {
        (async () => {
            try {
                const res = await getFlashDiscountSettings();
                if (res.success && res.data) {
                    setEnableAutoScroll(!!res.data.enableAutoScroll);
                    const ms = res.data.autoScrollIntervalMs || DEFAULT_SCROLL_MS;
                    setAutoScrollIntervalMs(ms);
                    setScrollMsDraft(String(ms));
                }
            } catch (e) {
                console.error(e);
            }
        })();
    }, []);

    const handleToggleFlashAutoScroll = async () => {
        const next = !enableAutoScroll;
        const intervalMs = scrollMsDraft !== "" ? Number(scrollMsDraft) : (autoScrollIntervalMs || DEFAULT_SCROLL_MS);
        setAutoScrollSaving(true);
        try {
            const res = await toggleFlashDealAutoScroll({
                enableAutoScroll: next,
                autoScrollIntervalMs: intervalMs,
            });
            if (res.success) {
                setEnableAutoScroll(!!res.data?.enableAutoScroll);
                const ms = res.data?.autoScrollIntervalMs || DEFAULT_SCROLL_MS;
                setAutoScrollIntervalMs(ms);
                setScrollMsDraft(String(ms));
                toast.success(next ? "Flash Deals auto scroll enabled on Home" : "Flash Deals auto scroll disabled");
            } else toast.error(res.message || "Failed to update auto scroll");
        } catch (e) {
            toast.error(e?.message || "Failed to update auto scroll");
        } finally {
            setAutoScrollSaving(false);
        }
    };

    const handleSaveFlashScrollMs = async () => {
        const intervalMs = Number(scrollMsDraft || autoScrollIntervalMs || DEFAULT_SCROLL_MS);
        if (!Number.isFinite(intervalMs) || intervalMs < 500) {
            toast.error("Scroll time must be at least 500 milliseconds");
            return;
        }
        setAutoScrollSaving(true);
        try {
            const res = await toggleFlashDealAutoScroll({
                enableAutoScroll,
                autoScrollIntervalMs: intervalMs,
            });
            if (res.success) {
                const ms = res.data?.autoScrollIntervalMs || intervalMs;
                setAutoScrollIntervalMs(ms);
                setScrollMsDraft(String(ms));
                toast.success("Scroll time updated");
            } else toast.error(res.message || "Failed to update scroll time");
        } catch (e) {
            toast.error(e?.message || "Failed to update scroll time");
        } finally {
            setAutoScrollSaving(false);
        }
    };

    const editingEntryRef = useRef(editingEntry);
    editingEntryRef.current = editingEntry;

    const excludeKeysRef = useRef(excludeKeys);
    excludeKeysRef.current = excludeKeys;

    const fetchProducts = useCallback(async (skip = 0) => {
        if (skip === 0) {
            setIsLoadingProducts(true);
            setHasMoreProducts(true);
        } else setIsFetchingMore(true);
        try {
            const currentExclude = excludeKeysRef.current;
            const editing = editingEntryRef.current;
            const keysForRequest = editing
                ? currentExclude.filter((k) => k !== `${editing.productId}_${editing.selectedWeight}`)
                : currentExclude;

            const res = await getProductsForFlashDealMapping({
                searchTerm: debouncedProductSearch,
                excludeKeys: keysForRequest,
                skip,
                limit: productsLimit,
            });
            if (res.success) {
                const fetched = res.data || [];
                if (skip === 0) setAvailableProducts(fetched);
                else setAvailableProducts((prev) => [...prev, ...fetched]);
                setHasMoreProducts(fetched.length === productsLimit);
                setProductsSkip(skip + fetched.length);
            }
        } finally {
            setIsLoadingProducts(false);
            setIsFetchingMore(false);
        }
    }, [debouncedProductSearch, productsLimit]);

    useEffect(() => {
        setProductsSkip(0);
        fetchProducts(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchProducts uses refs; deps are stable keys only
    }, [debouncedProductSearch, excludeKeysKey]);

    const openConfigModal = (product, option, entry = null) => {
        setSelectedProduct(product);
        setSelectedOption(option);
        setEditingEntry(entry);
        setDiscountPrice(entry ? String(entry.discountPrice) : "");
        setMinimumCartValue(entry ? String(entry.minimumCartValue ?? 0) : "");
        setMaxQty(entry ? String(entry.maxAllowedQuantityInOneOrder ?? 1) : "1");
        setConfigModalOpen(true);
    };

    const validateForm = () => {
        const flash = Number(discountPrice);
        const regular = Number(selectedOption?.discountPrice);
        if (!Number.isFinite(flash) || flash <= 0) {
            toast.error("Discount price must be greater than 0");
            return false;
        }
        if (Number.isFinite(regular) && flash >= regular) {
            toast.error("Discount price should be lower than regular discount price");
            return false;
        }
        if (!Number.isFinite(Number(minimumCartValue)) || Number(minimumCartValue) < 0) {
            toast.error("Minimum cart value must be 0 or greater");
            return false;
        }
        if (!Number.isFinite(Number(maxQty)) || Number(maxQty) < 1) {
            toast.error("Max quantity must be at least 1");
            return false;
        }
        return true;
    };

    const handleSave = async () => {
        if (!selectedProduct || !selectedOption || !validateForm()) return;
        setIsSaving(true);
        try {
            const payload = {
                productId: selectedProduct._id,
                selectedWeight: getOptionLabel(selectedOption),
                discountPrice: Number(discountPrice),
                minimumCartValue: Number(minimumCartValue || 0),
                maxAllowedQuantityInOneOrder: Number(maxQty || 1),
            };
            const res = editingEntry
                ? await updateFlashDealProduct(payload)
                : await addFlashDealProduct(payload);
            if (res.success) {
                toast.success(editingEntry ? "Flash deal updated" : "Flash deal product saved");
                setConfigModalOpen(false);
                fetchEntries();
            } else toast.error(res.message || "Failed to save");
        } catch (e) {
            toast.error(e.message || "Failed to save");
        } finally {
            setIsSaving(false);
        }
    };

    const handleRemove = async (entry) => {
        try {
            const res = await removeFlashDealProduct({
                productId: entry.productId,
                selectedWeight: entry.selectedWeight,
            });
            if (res.success) {
                toast.success("Product removed — prices restored");
                setDeleteConfirm(null);
                fetchEntries();
            } else toast.error(res.message || "Failed to remove");
        } catch (e) {
            toast.error("Failed to remove");
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="mb-8 flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-rose-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg">
                    <FaBolt className="text-white text-2xl" />
                </div>
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Flash Deals</h1>
                    <p className="text-gray-500 text-sm">Map products to flash pricing — same rules as Flash Discount in cart</p>
                </div>
            </div>

            <div className="bg-white rounded-xl border border-rose-100 shadow-sm p-4 mb-6">
                <div className="flex flex-wrap items-center gap-4">
                    <div className="flex-1 min-w-[200px]">
                        <h3 className="font-semibold text-gray-900">Enable auto scroll</h3>
                        <p className="text-xs text-gray-500 mt-1">
                            Auto-advance Flash Deal products on the customer Home page (not on Cart). Infinite loop scroll.
                        </p>
                    </div>
                    <button
                        type="button"
                        role="switch"
                        aria-checked={enableAutoScroll}
                        disabled={autoScrollSaving}
                        onClick={handleToggleFlashAutoScroll}
                        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                            enableAutoScroll ? "bg-rose-500" : "bg-gray-300"
                        } ${autoScrollSaving ? "opacity-50" : "cursor-pointer"}`}
                    >
                        <span
                            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                                enableAutoScroll ? "translate-x-5" : "translate-x-0.5"
                            }`}
                        />
                    </button>
                </div>
                {enableAutoScroll && (
                    <div className="mt-4 flex flex-wrap items-end gap-3 border-t border-rose-50 pt-4">
                        <div>
                            <label className="text-xs font-medium text-gray-600">Scroll time (milliseconds)</label>
                            <input
                                type="number"
                                min={500}
                                step={100}
                                className="mt-1 w-40 border rounded-lg px-3 py-2 text-sm"
                                placeholder={String(DEFAULT_SCROLL_MS)}
                                value={scrollMsDraft}
                                onChange={(e) => setScrollMsDraft(e.target.value)}
                            />
                            <p className="text-[10px] text-gray-400 mt-1">
                                Default {DEFAULT_SCROLL_MS} ms when enabling without a custom value
                            </p>
                        </div>
                        <button
                            type="button"
                            disabled={autoScrollSaving}
                            onClick={handleSaveFlashScrollMs}
                            className="px-4 py-2 text-sm font-semibold rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
                        >
                            Save interval
                        </button>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">Select Product & Option</h3>
                    <div className="relative mb-4">
                        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search products..."
                            value={productSearch}
                            onChange={(e) => setProductSearch(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-rose-500"
                        />
                    </div>
                    <div
                        className="h-[420px] overflow-auto border border-gray-200 rounded-lg"
                        onScroll={(e) => {
                            const el = e.currentTarget;
                            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50 && !isFetchingMore && hasMoreProducts) {
                                fetchProducts(productsSkip);
                            }
                        }}
                    >
                        {isLoadingProducts && productsSkip === 0 ? (
                            <div className="flex justify-center py-16"><div className="animate-spin h-8 w-8 border-t-2 border-rose-500 rounded-full" /></div>
                        ) : (
                            <div className="divide-y divide-gray-100">
                                {availableProducts.map((product) => (
                                    <div key={product._id} className="p-3">
                                        <p className="font-medium text-sm text-gray-900 mb-2">{product.productName}</p>
                                        <div className="flex flex-wrap gap-2">
                                            {(product.productOptions || []).map((opt, idx) => {
                                                const hints = getOptionPriceHints(opt);
                                                return (
                                                <button
                                                    key={idx}
                                                    type="button"
                                                    onClick={() => openConfigModal(product, opt)}
                                                    className="px-3 py-1.5 text-left text-xs font-semibold rounded-lg bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200"
                                                >
                                                    <span className="block">{getOptionLabel(opt)}</span>
                                                    {(hints.regularLabel || hints.pctLabel) && (
                                                        <span className="text-[10px] font-normal text-rose-600">{hints.regularLabel}{hints.pctLabel ? ` · ${hints.pctLabel}` : ""}</span>
                                                    )}
                                                </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                    <div className="flex gap-2 mb-4">
                        <div className="relative flex-1">
                            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search saved flash deals..."
                                value={listSearch}
                                onChange={(e) => setListSearch(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg"
                            />
                        </div>
                    </div>
                    {isLoading ? (
                        <div className="flex justify-center py-16"><div className="animate-spin h-8 w-8 border-t-2 border-rose-500 rounded-full" /></div>
                    ) : entries.length === 0 ? (
                        <p className="text-center text-gray-500 py-12">No flash deal products yet</p>
                    ) : (
                        <div className="space-y-3 max-h-[480px] overflow-auto">
                            {entries.map((entry) => (
                                <div key={`${entry.productId}_${entry.selectedWeight}`} className="flex items-center gap-3 p-3 border border-gray-100 rounded-lg">
                                    <div className="w-12 h-12 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0">
                                        {getProductPreview(entry) ? (
                                            <img src={getProductPreview(entry)} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-gray-400"><FaBoxes /></div>
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-sm truncate">{entry.productName}</p>
                                        <p className="text-xs text-gray-500">{entry.selectedWeight} · ₹{entry.discountPrice}</p>
                                        <p className="text-xs text-gray-400">Min cart ₹{entry.minimumCartValue} · Max {entry.maxAllowedQuantityInOneOrder}/order</p>
                                    </div>
                                    <button type="button" onClick={() => openConfigModal({ _id: entry.productId, productName: entry.productName }, { discountPrice: entry.regularDiscountPrice, weight: entry.selectedWeight }, entry)} className="p-2 text-gray-500 hover:text-rose-700"><FiEdit2 size={16} /></button>
                                    <button type="button" onClick={() => setDeleteConfirm(entry)} className="p-2 text-gray-400 hover:text-red-600"><FaTrash size={16} /></button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {configModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl w-full max-w-md p-6">
                        <div className="flex justify-between items-start mb-4">
                            <h2 className="text-lg font-bold">{editingEntry ? "Edit" : "Add"} Flash Deal</h2>
                            <button type="button" onClick={() => setConfigModalOpen(false)}><FaTimes /></button>
                        </div>
                        <p className="text-sm text-gray-600 mb-4">{selectedProduct?.productName} — {getOptionLabel(selectedOption)}</p>
                        <div className="space-y-3">
                            <div>
                                <label className="text-sm font-medium">Discount Price *</label>
                                <input type="number" value={discountPrice} onChange={(e) => setDiscountPrice(e.target.value)} className="w-full mt-1 border rounded-lg px-3 py-2" />
                            </div>
                            <div>
                                <label className="text-sm font-medium">Minimum Cart Value *</label>
                                <input type="number" value={minimumCartValue} onChange={(e) => setMinimumCartValue(e.target.value)} className="w-full mt-1 border rounded-lg px-3 py-2" />
                            </div>
                            <div>
                                <label className="text-sm font-medium">Max Qty In One Order *</label>
                                <input type="number" min={1} value={maxQty} onChange={(e) => setMaxQty(e.target.value)} className="w-full mt-1 border rounded-lg px-3 py-2" />
                            </div>
                        </div>
                        <div className="flex gap-3 mt-6">
                            <button type="button" onClick={() => setConfigModalOpen(false)} className="flex-1 py-2 border rounded-lg">Cancel</button>
                            <button type="button" onClick={handleSave} disabled={isSaving} className="flex-1 py-2 bg-rose-600 text-white rounded-lg font-semibold flex items-center justify-center gap-2">
                                {isSaving ? "Saving..." : <><FaCheck /> Save</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {deleteConfirm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl p-6 max-w-sm w-full">
                        <p className="font-semibold mb-2">Remove flash deal?</p>
                        <p className="text-sm text-gray-600 mb-4">Product will revert to original discount price.</p>
                        <div className="flex gap-3">
                            <button type="button" onClick={() => setDeleteConfirm(null)} className="flex-1 py-2 border rounded-lg">Cancel</button>
                            <button type="button" onClick={() => handleRemove(deleteConfirm)} className="flex-1 py-2 bg-red-600 text-white rounded-lg">Remove</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FlashDeals;
